/**
 * Transforms an alignment trimbim buffer by applying AlignmentSettings:
 *  - Reformats BillboardText labels using the label format / symbol settings
 *  - Applies vertical (Z) offset to billboard placements
 *  - Applies horizontal offset perpendicular to the alignment direction
 *  - Scales text style size by the labelSize multiplier
 *
 * All non-billboard geometry (GraphicsContainer line segments, etc.) is
 * copied faithfully from the source buffer.
 */
import { ByteBuffer } from 'flatbuffers';
import {
  GeometryType,
  Model as TrbModel,
} from '@technology/web3d-plugin-trimbim/dist/esnext/TrimBim_generated';
import { AlignmentSettings, DEFAULT_ALIGNMENT_SETTINGS } from '@trimble/types';
import {
  formatChainageDisplayValue,
  parseChainageValue,
} from '@trimble/utilities';
import { t } from '@trimble/i18n';
import { AlignmentTrimbimGenerator } from './alignment-trimbim-generator';
import {
  SectionChainageData,
  TrajectoryMetadata,
} from './trimbim-alignment.types';
import { parseStationEquationLabel } from './trimbim-alignment';
import {
  addPyramidMarkers,
  findCenterLineMaterialId,
  findClosestMarkup,
  BillboardMarkerInfo,
  Vec3,
  LineSeg,
  SegmentWithTransform,
} from './trimbim-alignment-markers';

export interface TransformOptions {
  alignmentSettings: AlignmentSettings;
  /** Number of decimal places used by the Alignment API when it generated labels. */
  lengthDecimals: number;
  /** Trajectory metadata — needed for positioning pyramid markers on the trajectory. */
  trajectoryMetadata?: TrajectoryMetadata;
}

/**
 * Formats billboard text using the current alignment settings.
 *
 * - Station equation (gap / overlength): detected from the text pattern and
 *   formatted using a translated template with formatted start/end chainage
 *   values.  The delta part is kept verbatim from the original API label.
 * - Regular markup: formats the chainage value using the label format.
 */
function formatBillboardText(
  originalText: string,
  alignmentSettings: AlignmentSettings,
  lengthDecimals: number
): string {
  const eqInfo = parseStationEquationLabel(originalText);
  if (eqInfo) {
    const fmtStart = formatChainageDisplayValue(
      eqInfo.startMeters,
      alignmentSettings.labelFormat,
      lengthDecimals
    );
    const fmtEnd = formatChainageDisplayValue(
      eqInfo.endMeters,
      alignmentSettings.labelFormat,
      lengthDecimals
    );
    const key =
      eqInfo.type === 'gap'
        ? 'shared.ui.stationEquation.gap'
        : 'shared.ui.stationEquation.overlength';
    return t(key, { delta: eqInfo.deltaText, start: fmtStart, end: fmtEnd });
  }

  const chainageValue = parseChainageValue(originalText);
  return isNaN(chainageValue)
    ? originalText
    : formatChainageDisplayValue(
        chainageValue,
        alignmentSettings.labelFormat,
        lengthDecimals
      );
}

/**
 * Reads the source trimbim ArrayBuffer, clones all geometry faithfully, and
 * rebuilds BillboardText definitions with the user's label format settings.
 *
 * @returns A new ArrayBuffer containing the transformed trimbim, or the
 *          original buffer unchanged if no transformation is needed.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 161 > 15 allowed; refactor later
export function transformAlignmentTrimbim(
  sourceBuffer: ArrayBuffer,
  options: TransformOptions
): ArrayBuffer {
  const { alignmentSettings, lengthDecimals, trajectoryMetadata } = options;

  const src = TrbModel.getRootAsModel(
    new ByteBuffer(new Uint8Array(sourceBuffer))
  );

  const srcGeometry = src.geometry();
  const srcProperties = src.properties();
  const srcEntities = src.entities();
  if (!srcGeometry || !srcProperties || !srcEntities) {
    return sourceBuffer;
  }

  const gen = new AlignmentTrimbimGenerator();

  // ── 1. Clone materials ──────────────────────────────────────────────
  for (let i = 0; i < srcGeometry.surfaceMaterialsLength(); i++) {
    const mat = srcGeometry.surfaceMaterials(i);
    if (!mat) continue;
    gen.addMaterial({
      r: mat.r(),
      g: mat.g(),
      b: mat.b(),
      a: mat.a(),
      textureId: mat.textureId(),
      sidedness: mat.sidedness(),
      lineStyle: mat.lineStyle(),
      metallic: mat.metallic(),
      roughness: mat.roughness(),
    });
  }

  // ── 2. Clone text styles (apply labelSize scaling) ──────────────────
  for (let i = 0; i < srcGeometry.textStylesLength(); i++) {
    const textStyle = srcGeometry.textStyles(i);
    if (!textStyle) continue;
    gen.addTextStyle({
      font: textStyle.font() ?? '',
      size: textStyle.size() * alignmentSettings.labelSize,
      options: textStyle.options(),
      verticalAlign: textStyle.verticalAlign(),
      horizontalAlign: textStyle.horizontalAlign(),
      rotationAngle: textStyle.rotationAngle(),
      layout: textStyle.layout(),
      alignment: textStyle.alignment(),
      maxAutoScale: textStyle.maxAutoScale(),
      verticalDistanceToObject: textStyle.verticalDistanceToObject(),
    });
  }

  // ── 3. Clone layers and layer visibility ─────────────────────────────
  for (let i = 0; i < srcGeometry.layersLength(); i++) {
    gen.addLayer(srcGeometry.layers(i) ?? 'Layer');
  }
  for (let i = 0; i < srcGeometry.layerDefaultVisibilityLength(); i++) {
    gen.addLayerVisibility(srcGeometry.layerDefaultVisibility(i) ?? true);
  }

  const entitySectionData = new Map<number, SectionChainageData>();
  if (trajectoryMetadata?.sectionChainages) {
    for (const section of trajectoryMetadata.sectionChainages) {
      entitySectionData.set(section.entityId, section);
    }
  }

  // ── 4. Single-pass instance scan ────────────────────────────────────
  // Collects GC segments (for pyramid positioning), billboard marker info
  // (for pyramid generation), and billboard placement data (for offset
  // computation) — all in one iteration over instances.
  // Billboards whose corresponding markup has no label are excluded.
  const entitySegmentsWithTransform = new Map<number, SegmentWithTransform[]>();
  const billboardMarkersInfo: BillboardMarkerInfo[] = [];
  const billboardPlacementIds = new Set<number>();
  const billboardPlacementEntityAndChainage: Array<{
    placementId: number;
    entityId: number;
    chainageValue: number;
    label: string;
  }> = [];
  const skippedBillboardDefinitions = new Set<number>();
  const skippedBillboardInstances = new Set<number>();

  for (let i = 0; i < srcGeometry.instancesLength(); i++) {
    const inst = srcGeometry.instances(i);
    if (!inst) continue;
    const definition = srcGeometry.definitions(inst.definitionId());
    if (!definition) continue;

    if (definition.type() === GeometryType.GraphicsContainer) {
      const graphicsContainer = srcGeometry.graphicsContainers(
        definition.index()
      );
      if (!graphicsContainer || graphicsContainer.lineSegmentsLength() === 0)
        continue;

      const localPlacement = srcGeometry.localPlacements(
        inst.localPlacementId()
      );
      const placementOrigin = localPlacement?.origin();
      const placementXAxis = localPlacement?.xAxis();
      const placementYAxis = localPlacement?.yAxis();
      const xAxis: Vec3 = placementXAxis
        ? {
            x: placementXAxis.x(),
            y: placementXAxis.y(),
            z: placementXAxis.z(),
          }
        : { x: 1, y: 0, z: 0 };
      const yAxis: Vec3 = placementYAxis
        ? {
            x: placementYAxis.x(),
            y: placementYAxis.y(),
            z: placementYAxis.z(),
          }
        : { x: 0, y: 1, z: 0 };
      const placement = {
        origin: placementOrigin
          ? {
              x: placementOrigin.x(),
              y: placementOrigin.y(),
              z: placementOrigin.z(),
            }
          : ({ x: 0, y: 0, z: 0 } as Vec3),
        xAxis,
        yAxis,
        zAxis: {
          x: xAxis.y * yAxis.z - xAxis.z * yAxis.y,
          y: xAxis.z * yAxis.x - xAxis.x * yAxis.z,
          z: xAxis.x * yAxis.y - xAxis.y * yAxis.x,
        },
      };

      const entityId = inst.entityId();
      let segmentList = entitySegmentsWithTransform.get(entityId);
      if (!segmentList) {
        segmentList = [];
        entitySegmentsWithTransform.set(entityId, segmentList);
      }
      for (let j = 0; j < graphicsContainer.lineSegmentsLength(); j++) {
        const lineSeg = graphicsContainer.lineSegments(j);
        if (!lineSeg) continue;
        const segStart = lineSeg.start();
        const segEnd = lineSeg.end();
        if (!segStart || !segEnd) continue;
        segmentList.push({
          seg: {
            start: { x: segStart.x(), y: segStart.y(), z: segStart.z() },
            end: { x: segEnd.x(), y: segEnd.y(), z: segEnd.z() },
          },
          placement,
        });
      }
    } else if (definition.type() === GeometryType.BillboardText) {
      const localPlacement = srcGeometry.localPlacements(
        inst.localPlacementId()
      );
      if (!localPlacement) continue;
      const origin = localPlacement.origin();
      if (!origin) continue;

      const placementId = inst.localPlacementId();
      const placementOrigin: Vec3 = {
        x: origin.x(),
        y: origin.y(),
        z: origin.z(),
      };

      const billboard = srcGeometry.billboardTexts(definition.index());
      const billboardText = billboard?.text() ?? '';
      const eq = parseStationEquationLabel(billboardText);
      let chainage: number;
      if (eq) {
        // Use the physical position chainage for markup lookup / pyramid
        // placement. For gaps, the billboard sits at endMeters (the "ahead"
        // side). For overlengths, the billboard sits at endMeters (the
        // "approaching" / chainageBefore side).
        chainage = eq.endMeters;
      } else {
        chainage = parseChainageValue(billboardText);
      }

      const sectionData = entitySectionData.get(inst.entityId());
      // For station equation billboards, match by label text to find the
      // exact markup (chainage-based lookup can't distinguish overlength
      // markups from regular markups at the same chainage value).
      const closestMarkup = sectionData
        ? (sectionData.markups.find((m) => m.label === billboardText) ??
          findClosestMarkup(
            sectionData.markups,
            isNaN(chainage) ? 0 : chainage
          ))
        : undefined;
      const hasLabel = !closestMarkup || closestMarkup.label !== undefined;

      if (hasLabel) {
        billboardMarkersInfo.push({
          entityId: inst.entityId(),
          placementOrigin,
          chainageValue: isNaN(chainage) ? 0 : chainage,
          surfaceMaterialId: inst.surfaceMaterialId(),
          layerId: inst.layerId(),
          label: billboardText || undefined,
        });

        billboardPlacementIds.add(placementId);
        billboardPlacementEntityAndChainage.push({
          placementId,
          entityId: inst.entityId(),
          chainageValue: isNaN(chainage) ? 0 : chainage,
          label: billboardText,
        });
      } else {
        skippedBillboardDefinitions.add(inst.definitionId());
        skippedBillboardInstances.add(i);
      }
    }
  }

  // Precompute entity global origin Z for world → entity-local conversion.
  const entityGlobalOriginZ = new Map<number, number>();
  for (let i = 0; i < srcEntities.entitiesLength(); i++) {
    const ent = srcEntities.entities(i);
    if (!ent) continue;
    entityGlobalOriginZ.set(i, ent.globalPlacement()?.origin()?.z() ?? 0);
  }

  // Look up worldPosition.z and perpendicularVector from MarkupChainageData.
  const perpendicularByPlacement = new Map<number, Vec3>();
  const trajectoryZByPlacement = new Map<number, number>();
  for (const billboardInfo of billboardPlacementEntityAndChainage) {
    const sectionData = entitySectionData.get(billboardInfo.entityId);
    if (!sectionData) continue;

    const closestMarkup =
      sectionData.markups.find((m) => m.label === billboardInfo.label) ??
      findClosestMarkup(sectionData.markups, billboardInfo.chainageValue);
    if (!closestMarkup) continue;

    if (closestMarkup.worldPosition) {
      const globalZ = entityGlobalOriginZ.get(billboardInfo.entityId) ?? 0;
      trajectoryZByPlacement.set(
        billboardInfo.placementId,
        closestMarkup.worldPosition.z - globalZ
      );
    }

    if (closestMarkup.perpendicularVector) {
      perpendicularByPlacement.set(
        billboardInfo.placementId,
        closestMarkup.perpendicularVector
      );
    }
  }

  // ── 5. Clone geometry definitions ─────────────────────────────────────
  for (let i = 0; i < srcGeometry.definitionsLength(); i++) {
    const srcDef = srcGeometry.definitions(i);
    if (!srcDef) continue;

    // Clone the source's bounding box for this definition
    const srcBoundingBox = srcDef.boundingBox();
    const srcBbMin = srcBoundingBox?.min();
    const srcBbMax = srcBoundingBox?.max();
    const boundingBoxMin: Vec3 = srcBbMin
      ? { x: srcBbMin.x(), y: srcBbMin.y(), z: srcBbMin.z() }
      : { x: 0, y: 0, z: 0 };
    const boundingBoxMax: Vec3 = srcBbMax
      ? { x: srcBbMax.x(), y: srcBbMax.y(), z: srcBbMax.z() }
      : { x: 0, y: 0, z: 0 };

    switch (srcDef.type()) {
      case GeometryType.GraphicsContainer: {
        const srcGc = srcGeometry.graphicsContainers(srcDef.index());
        if (!srcGc) break;

        const lineSegments: LineSeg[] = [];
        for (let j = 0; j < srcGc.lineSegmentsLength(); j++) {
          const lineSeg = srcGc.lineSegments(j);
          if (!lineSeg) continue;
          const segStart = lineSeg.start();
          const segEnd = lineSeg.end();
          if (!segStart || !segEnd) continue;
          lineSegments.push({
            start: { x: segStart.x(), y: segStart.y(), z: segStart.z() },
            end: { x: segEnd.x(), y: segEnd.y(), z: segEnd.z() },
          });
        }

        const arcs: Array<{
          start: Vec3;
          mid: Vec3;
          end: Vec3;
          normal: Vec3;
        }> = [];
        for (let j = 0; j < srcGc.arcsLength(); j++) {
          const arc = srcGc.arcs(j);
          if (!arc) continue;
          const center = arc.center();
          const axis = arc.axis();
          const refDirection = arc.refDirection();
          if (!center || !axis || !refDirection) continue;
          arcs.push({
            start: { x: center.x(), y: center.y(), z: center.z() },
            mid: { x: axis.x(), y: axis.y(), z: axis.z() },
            end: {
              x: refDirection.x(),
              y: refDirection.y(),
              z: refDirection.z(),
            },
            normal: { x: arc.radius(), y: arc.angle(), z: 0 },
          });
        }

        gen.addGraphicsContainer(
          { lineSegments, arcs },
          boundingBoxMin,
          boundingBoxMax
        );
        break;
      }

      case GeometryType.BillboardText: {
        if (skippedBillboardDefinitions.has(i)) {
          gen.addGraphicsContainer(
            { lineSegments: [], arcs: [] },
            boundingBoxMin,
            boundingBoxMax
          );
          break;
        }

        const srcBillboard = srcGeometry.billboardTexts(srcDef.index());
        if (!srcBillboard) break;

        const originalText = srcBillboard.text() ?? '';
        const newText = formatBillboardText(
          originalText,
          alignmentSettings,
          lengthDecimals
        );

        gen.addBillboardText(
          { textStyleId: srcBillboard.textStyleId(), text: newText },
          boundingBoxMin,
          boundingBoxMax
        );
        break;
      }

      default:
        // Unknown geometry type — emit a placeholder GraphicsContainer with
        // no segments to keep definition indices aligned with instances.
        gen.addGraphicsContainer(
          { lineSegments: [], arcs: [] },
          boundingBoxMin,
          boundingBoxMax
        );
        break;
    }
  }

  // ── 6. Clone placements (apply vertical + horizontal offset) ────────
  const needsHorizontalOffset = alignmentSettings.horizontalOffset !== 0;

  for (let i = 0; i < srcGeometry.localPlacementsLength(); i++) {
    const srcPlacement = srcGeometry.localPlacements(i);
    if (!srcPlacement) continue;
    const origin = srcPlacement.origin();
    const xAxis = srcPlacement.xAxis();
    const yAxis = srcPlacement.yAxis();
    if (!origin || !xAxis || !yAxis) continue;

    let originX = origin.x();
    let originY = origin.y();
    let originZ = origin.z();

    if (billboardPlacementIds.has(i)) {
      const trajectoryZ = trajectoryZByPlacement.get(i);
      if (trajectoryZ !== undefined) {
        originZ = trajectoryZ + alignmentSettings.verticalOffset;
      } else {
        originZ +=
          alignmentSettings.verticalOffset -
          DEFAULT_ALIGNMENT_SETTINGS.verticalOffset;
      }

      if (needsHorizontalOffset) {
        const perpendicular = perpendicularByPlacement.get(i);
        if (perpendicular) {
          originX += alignmentSettings.horizontalOffset * perpendicular.x;
          originY += alignmentSettings.horizontalOffset * perpendicular.y;
        }
      }
    }

    gen.addPlacement(
      { x: originX, y: originY, z: originZ },
      { x: xAxis.x(), y: xAxis.y(), z: xAxis.z() },
      { x: yAxis.x(), y: yAxis.y(), z: yAxis.z() }
    );
  }

  // ── 7. Clone instances (skip billboards without labels) ─────────────
  for (let i = 0; i < srcGeometry.instancesLength(); i++) {
    if (skippedBillboardInstances.has(i)) continue;
    const inst = srcGeometry.instances(i);
    if (!inst) continue;
    gen.addInstance({
      localPlacementId: inst.localPlacementId(),
      entityId: inst.entityId(),
      definitionId: inst.definitionId(),
      surfaceMaterialId: inst.surfaceMaterialId(),
      layerId: inst.layerId(),
    });
  }

  // ── 8. Clone entities ───────────────────────────────────────────────
  for (let i = 0; i < srcEntities.entitiesLength(); i++) {
    const entity = srcEntities.entities(i);
    if (!entity) continue;
    const globalPlacement = entity.globalPlacement();
    const globalOrigin = globalPlacement?.origin();
    const globalXAxis = globalPlacement?.xAxis();
    const globalYAxis = globalPlacement?.yAxis();

    gen.addEntity({
      idType: entity.type(),
      idIndex: entity.identifierIndex(),
      classIndex: entity.classIndex(),
      globalOrigin: globalOrigin
        ? { x: globalOrigin.x(), y: globalOrigin.y(), z: globalOrigin.z() }
        : { x: 0, y: 0, z: 0 },
      xAxis: globalXAxis
        ? { x: globalXAxis.x(), y: globalXAxis.y(), z: globalXAxis.z() }
        : { x: 1, y: 0, z: 0 },
      yAxis: globalYAxis
        ? { x: globalYAxis.x(), y: globalYAxis.y(), z: globalYAxis.z() }
        : { x: 0, y: 1, z: 0 },
    });
  }

  // Clone string identifiers
  for (let i = 0; i < srcEntities.stringIdentifiersLength(); i++) {
    gen.addStringIdentifier(srcEntities.stringIdentifiers(i) ?? '');
  }

  // Clone entity classes
  for (let i = 0; i < srcEntities.entityClassesLength(); i++) {
    gen.addEntityClass(srcEntities.entityClasses(i) ?? 'Class');
  }

  // ── 9. Clone properties ─────────────────────────────────────────────
  cloneProperties(srcProperties, gen);

  // ── 10. Add pyramid markers at each billboard's trajectory point ────
  // Use the center-line material (or first GC material as fallback) so
  // pyramids match the trajectory color even without section coloring.
  const pyramidMaterialId = findCenterLineMaterialId(
    srcGeometry,
    trajectoryMetadata?.trimbimProperties?.alignmentProperties
  );
  addPyramidMarkers(
    gen,
    billboardMarkersInfo,
    entitySectionData,
    entitySegmentsWithTransform,
    pyramidMaterialId
  );

  // ── 11. Generate ────────────────────────────────────────────────────
  return gen.generate();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Copies the entire property system from the source model into the generator.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 18 > 15 allowed; refactor later
function cloneProperties(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FlatBuffers ModelProperties type
  srcProps: any,
  gen: AlignmentTrimbimGenerator
): void {
  // Property set names
  for (let i = 0; i < srcProps.propertySetNamesLength(); i++) {
    gen.addPropertySetName(srcProps.propertySetNames(i) ?? '');
  }

  // Property names
  for (let i = 0; i < srcProps.propertyNamesLength(); i++) {
    gen.addPropertyName(srcProps.propertyNames(i) ?? '');
  }

  // String values
  for (let i = 0; i < srcProps.stringValuesLength(); i++) {
    gen.addStringValue(srcProps.stringValues(i) ?? '');
  }

  // Property set definitions
  for (let i = 0; i < srcProps.definitionsLength(); i++) {
    const def = srcProps.definitions(i);
    if (!def) continue;

    const properties: Array<{ name: number; type: number }> = [];
    for (let j = 0; j < def.propertiesLength(); j++) {
      const prop = def.properties(j);
      if (!prop) continue;
      properties.push({
        name: prop.name(),
        type: prop.type() as number,
      });
    }

    gen.addPropertySetDefinition({
      name: def.name(),
      properties,
    });
  }

  // Property set bindings
  for (let i = 0; i < srcProps.propertySetBindingsLength(); i++) {
    const binding = srcProps.propertySetBindings(i);
    if (!binding) continue;

    const values: number[] = [];
    for (let j = 0; j < binding.valuesLength(); j++) {
      values.push(binding.values(j) ?? 0);
    }

    const entities: number[] = [];
    for (let j = 0; j < binding.entitiesLength(); j++) {
      entities.push(binding.entities(j) ?? 0);
    }

    gen.addPropertySetBinding({
      definition: binding.definition(),
      values,
      entities,
    });
  }
}
